import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "../../components";
import OnboardContext from "./context";
import _ from "lodash";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";

import { ReactComponent as AllowSvg } from "../../assets/auth/onboard/accept.svg";
import { ReactComponent as UnallowSvg } from "../../assets/auth/onboard/x-circle.svg";
import { ReactComponent as AllowWhiteSvg } from "../../assets/auth/onboard/accept (1).svg";
import { ReactComponent as UnallowWhiteSvg } from "../../assets/auth/onboard/x-circle (1).svg";

const planOrders = ["Guest", "Trial", "Freelancer", "Business", "Enterprise"];

const SelectPlan = ({ onNavigateToContact, ...props }) => {
  const { plans, currentPlan, selectedPlan, setSelectedPlan } =
    useContext(OnboardContext);
  const { getLang, user } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationPlan = getLang(langComponent.plan);
  const swiperRef = useRef(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(2);

  const getLabel = (limit) => {
    const { name, value } = limit;

    let toReturn = "-";

    switch (name) {
      case "uploads":
        toReturn = Translation("Design uploads");
        break;
      case "commissions":
        toReturn = Translation("Upload size");
        break;
      case "users":
        toReturn = Translation("Users");
        break;
      case "panoramaSize":
        toReturn = "Max panorama size";
        break;
      case "workExchangeSize":
        toReturn = "Max work exchange size";
        break;
      default:
        break;
    }

    return toReturn;
  };

  const getLimitDisplayText = (limit) => {
    const { name, value } = limit;

    let toReturn = "-";

    switch (name) {
      case "uploads":
        if (value === 0) {
          toReturn = Translation("Unlimited");
          break;
        }

        if (value) {
          toReturn = value;
          break;
        }

        break;
      case "panoramaSize":
        if (value) {
          toReturn = `${value * 1000}MB`;
        }
        break;
      case "workExchangeSize":
        if (value) {
          toReturn = `${value}GB`;
        }
        break;
      case "cappedPrice":
        if (value === null) {
          break;
        }

        if (value === 0) {
          toReturn = Translation("Unlimited");
          break;
        }

        toReturn = `${value / 1000}K THB`;
        break;
      case "users":
        if (value === null) {
          break;
        }

        if (value) {
          toReturn = `${Translation("Up to")} ${value} ${Translation("users")}`;
          break;
        }
        break;
    }

    return toReturn;
  };

  /**
   * Get initial slide by checking selectedPlan then currentPlan else default to 2
   * @returns Initial slide index
   */
  const getInitialSlide = (plans) => {
    // * Check selectedPlan
    if (selectedPlan) {
      return plans.findIndex((p) => p._id === selectedPlan._id);
    }

    if (currentPlan) {
      return plans.findIndex((p) => p._id === currentPlan);
    }

    return 2;
  };

  const getTrialInfo = (plan) => {
    if (!["Freelancer", "Business"].includes(plan.name)) {
      return <></>;
    }

    if (currentPlan && plan._id === currentPlan) {
      return <></>;
    }

    // * Check if trial is available
    const _usedTrial = user?.usedTrial ?? false;
    const _trialEndOn = user?.trialEndOn ? true : false;

    if (_usedTrial) {
      return <></>;
    }

    if (!_trialEndOn) {
      return <></>;
    }

    return (
      <label className="onboard-select-plan-lists-item-trial">
        <span style={{ fontWeight: 500, fontSize: 22 }}>
          Free trial for 3 months!
        </span>
        <br />
        <span style={{ fontSize: 18 }}>No credit card required</span>
      </label>
    );
  };

  const getPlanCTA = (plan) => {
    if (currentPlan && plan._id === currentPlan) {
      return (
        <label className="onboard-select-plan-lists-item-current-plan">
          Current Plan
        </label>
      );
    }

    if (plan.name === "Enterprise") {
      return (
        <div className="onboard-select-plan-lists-item-button">Coming Soon</div>
      );
    }

    if (plan.name === "Enterprise")
      return (
        <div className="onboard-select-plan-lists-item-button">
          <Button
            className="onboard-select-plan-lists-item-cta"
            label={Translation("Contact us")}
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToContact();
            }}
          />
        </div>
      );

    return (
      <div
        className="onboard-select-plan-lists-item-button"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedPlan(plan);
        }}
      >
        <Button
          className="onboard-select-plan-lists-item-cta"
          label={TranslationCommon("Choose")}
        />
      </div>
    );
  };

  const getLimitIcon = (allow, selected) => {
    const className = "onboard-select-plan-lists-item-limit__icon";

    if (allow && selected)
      return <AllowWhiteSvg className={className} width={18} />;

    if (allow && !selected)
      return <AllowSvg className={className} width={18} />;

    if (!allow && selected)
      return (
        <UnallowWhiteSvg
          className={className}
          width={19}
          style={{ marginLeft: -1 }}
        />
      );

    if (!allow && !selected)
      return (
        <UnallowSvg
          className={className}
          width={19}
          style={{ marginLeft: -1 }}
        />
      );
  };

  // * Sort plans
  let _plans = _.cloneDeep(plans).sort((a, b) => {
    const aIndex = planOrders.findIndex((p) => p === a.name);
    const bIndex = planOrders.findIndex((p) => p === b.name);

    return bIndex < aIndex ? 1 : -1;
  });

  return (
    <div className="onboard-select-plan">
      <h1 className="onboard-select-plan__header">
        {Translation("Select Plan")}
      </h1>
      <Swiper
        effect={"coverflow"}
        grabCursor={true}
        centeredSlides={true}
        slidesPerView={1}
        initialSlide={getInitialSlide(_plans)}
        coverflowEffect={{
          rotate: 0,
          stretch: 50,
          depth: 100,
          slideShadows: false,
        }}
        ref={swiperRef}
        modules={[EffectCoverflow, Pagination]}
        className="onboard-select-plan-lists"
        onSlideChange={(swiper) => {
          setCurrentSlideIndex(swiper.activeIndex);
        }}
        breakpoints={{
          560: {
            slidesPerView: 2,
          },
          1024: { slidesPerView: 3 },
          1280: {
            slidesPerView: 4,
          },
        }}
      >
        {_plans.map((plan, index) => {
          const {
            name,
            desc,
            price,
            limit: { uploads, commissions, panoramaSize, workExchangeSize },
          } = plan;

          let priceToDisplay = Translation("Get in touch");

          if (price === -1) {
            priceToDisplay = "30 days";
          }

          if (price === 0) {
            priceToDisplay = Translation("Free");
          }

          if (price > 0) {
            priceToDisplay = `${price.toLocaleString()}`;
          }

          const limits = [
            { name: "uploads", value: uploads },
            { name: "panoramaSize", value: panoramaSize },
            { name: "workExchangeSize", value: workExchangeSize },
          ];

          const otherLimits = [
            { name: "Users", allow: true },
            { name: "Explore", allow: true },
            { name: "Buy", allow: true },
            { name: "Hire", allow: false },
            { name: "Free", allow: true },
          ];

          return (
            <SwiperSlide>
              <div
                className={`onboard-select-plan-lists-item`}
                key={`plan-${index}`}
                onClick={() => {
                  swiperRef.current.swiper.slideTo(index);
                }}
              >
                <div className="onboard-select-plan-lists-item-title">
                  {TranslationPlan(name)}
                  {name === "Freelancer" && (
                    <span className="onboard-select-plan-lists-item-title__popular">
                      Popular
                    </span>
                  )}
                </div>
                <div className="onboard-select-plan-lists-item__price">
                  {price > 0 && <>฿</>}
                  {priceToDisplay}
                  {price > 0 && <span>per month</span>}
                </div>
                <div className="onboard-select-plan-lists-item-desc">
                  {desc}
                </div>
                <div className="onboard-select-plan-lists-item__separator" />
                {limits.map((limit, lIndex) => {
                  return (
                    <div
                      className="onboard-select-plan-lists-item-limits-values"
                      key={`plan-${index}-limit-${lIndex}`}
                    >
                      <label className="onboard-select-plan-lists-item-limits-values__name">
                        {getLabel(limit)}
                      </label>
                      <label className="onboard-select-plan-lists-item-limits-values__value">
                        {getLimitDisplayText(limit)}
                      </label>
                    </div>
                  );
                })}
                {otherLimits.map(({ name, allow }) => (
                  <div className="onboard-select-plan-lists-item-limit">
                    {getLimitIcon(allow, currentSlideIndex === index)}
                    <span className="onboard-select-plan-lists-item-limit__title">
                      {name}
                    </span>
                  </div>
                ))}
                {getTrialInfo(plan)}
                {getPlanCTA(plan)}
                {currentSlideIndex !== index && (
                  <div className="onboard-select-plan-lists-item__overlay" />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

SelectPlan.propTypes = {
  onNavigateToContact: PropTypes.func,
};

SelectPlan.defaultProps = {
  onNavigateToContact: () => {},
};

export default SelectPlan;
